// @flow
import { of } from 'rxjs';
import { catchError, mergeMap, map } from 'rxjs/operators';
import update from 'immutability-helper';
import { getNotificationsApi, putNotificationsApi } from './api';
import { setNewAlert } from '../../alert/duck';
import { ofType } from 'redux-observable';

import type { Observable } from 'rxjs';
import type { HTTPError } from '../../../error/index';
import type { Settings } from '../../../entities';

export type NotificationsAction = {
  +type: string;
  +notificationsSettings?: Settings;
  +error?: HTTPError;
};

export type NotificationsState = {
  +isFetchingNotificationsSettings?: boolean;
  +isUpdatingNotificationsSettings?: boolean;
  +notificationsSettings?: Settings;
  +fetchNotificationsSettingsError?: HTTPError;
  +updateNotificationsSettingsError?: HTTPError;
};

//Actions
const NOTIFICATIONS_GET_NOTIFICATIONS_REQUEST = 'NOTIFICATIONS_GET_NOTIFICATIONS_REQUEST';
const NOTIFICATIONS_GET_NOTIFICATIONS_SUCCESS = 'NOTIFICATIONS_GET_NOTIFICATIONS_SUCCESS';
const NOTIFICATIONS_GET_NOTIFICATIONS_ERROR = 'NOTIFICATIONS_GET_NOTIFICATIONS_ERROR';
const NOTIFICATIONS_PUT_NOTIFICATIONS_REQUEST = 'NOTIFICATIONS_PUT_NOTIFICATIONS_REQUEST';
const NOTIFICATIONS_PUT_NOTIFICATIONS_SUCCESS = 'NOTIFICATIONS_PUT_NOTIFICATIONS_SUCCESS';
const NOTIFICATIONS_PUT_NOTIFICATIONS_ERROR = 'NOTIFICATIONS_PUT_NOTIFICATIONS_ERROR';

//Actions creators
export function fetchNotifications(): NotificationsAction {
  return {
    type: NOTIFICATIONS_GET_NOTIFICATIONS_REQUEST,
  };
}

export function fetchNotificationsSuccess(notificationsSettings: Settings): NotificationsAction {
  return {
    type: NOTIFICATIONS_GET_NOTIFICATIONS_SUCCESS,
    notificationsSettings,
  };
}

export function fetchNotificationsError(error: HTTPError): NotificationsAction {
  return {
    type: NOTIFICATIONS_GET_NOTIFICATIONS_ERROR,
    error,
  };
}

export function editNotifications(notificationsSettings: Settings): NotificationsAction {
  return {
    type: NOTIFICATIONS_PUT_NOTIFICATIONS_REQUEST,
    notificationsSettings,
  };
}

export function editNotificationsSuccess(notificationsSettings: Settings): NotificationsAction {
  return {
    type: NOTIFICATIONS_PUT_NOTIFICATIONS_SUCCESS,
    notificationsSettings,
  };
}

export function editNotificationsError(error: HTTPError): NotificationsAction {
  return {
    type: NOTIFICATIONS_PUT_NOTIFICATIONS_ERROR,
    error,
  };
}

export const actions = {
  NOTIFICATIONS_GET_NOTIFICATIONS_REQUEST,
  NOTIFICATIONS_GET_NOTIFICATIONS_SUCCESS,
  NOTIFICATIONS_GET_NOTIFICATIONS_ERROR,
  NOTIFICATIONS_PUT_NOTIFICATIONS_REQUEST,
  NOTIFICATIONS_PUT_NOTIFICATIONS_SUCCESS,
  NOTIFICATIONS_PUT_NOTIFICATIONS_ERROR,
  fetchNotifications,
  fetchNotificationsSuccess,
  fetchNotificationsError,
  editNotifications,
  editNotificationsSuccess,
  editNotificationsError,
};

export const initialState: NotificationsState = {};

//Reducer
export default function reducer(state: NotificationsState = initialState,
                                action: NotificationsAction): NotificationsState {
  switch (action.type) {
    case NOTIFICATIONS_GET_NOTIFICATIONS_REQUEST:
      return {
        isFetchingNotificationsSettings: true,
      };
    case NOTIFICATIONS_GET_NOTIFICATIONS_SUCCESS:
      return {
        notificationsSettings: action.notificationsSettings,
      };
    case NOTIFICATIONS_GET_NOTIFICATIONS_ERROR:
      return {
        fetchNotificationsSettingsError: action.error,
      };
    case NOTIFICATIONS_PUT_NOTIFICATIONS_REQUEST:
      return update(
        state, {
          isUpdatingNotificationsSettings: { $set: true },
        }
      );
    case NOTIFICATIONS_PUT_NOTIFICATIONS_SUCCESS:
      return update(
        state, {
          $unset: ['isUpdatingNotificationsSettings'],
          notificationsSettings: { $set: action.notificationsSettings },
        }
      );
    case NOTIFICATIONS_PUT_NOTIFICATIONS_ERROR:
      return {
        updateNotificationsSettingsError: action.error,
      };
    default:
      return state;
  }
}

//Epics
export function fetchNotificationSettingsEpic(action$: Observable<NotificationsAction>): Observable<NotificationsAction> {
  return action$.pipe(
    ofType(NOTIFICATIONS_GET_NOTIFICATIONS_REQUEST),
    mergeMap(() => getNotificationsApi().pipe(
      map((notificationsSettings: Settings) => fetchNotificationsSuccess(notificationsSettings)),
      catchError((e: HTTPError) => of(fetchNotificationsError(e))))
    ));
}

export function editNotificationsSettingsEpic(action$: Observable<NotificationsAction>): Observable<NotificationsAction> {
  return action$.pipe(
    ofType(NOTIFICATIONS_PUT_NOTIFICATIONS_REQUEST),
    mergeMap(({ notificationsSettings }) => putNotificationsApi(notificationsSettings).pipe(
      map((notificationsSettingsResponse: Settings) => editNotificationsSuccess(notificationsSettingsResponse)),
      catchError((e: HTTPError) => {
        if (Math.round(e.code / 100) === 5) {
          return of(editNotificationsError(e),
            setNewAlert('preferences', 'error', undefined, 'httpErrors.post.500'),
          );
        } else if (e.code === 400) {
          return of(editNotificationsError(e),
            setNewAlert('preferences', 'error', undefined, 'httpErrors.post.400'),
          );
        }
        return of(editNotificationsError(e));
      }))
    ));
}



// WEBPACK FOOTER //
// ./src/modules/preferences/notifications/duck.js